import { useState } from "react";
import { Link, Navigate, createFileRoute } from "@tanstack/react-router";

import { ApiError, auth } from "~/lib/api";
import { desktopRequest } from "~/lib/desktop-intent";
import { useAuthStore } from "~/lib/auth-store";
import { AuthShell, Field, inputCls, primaryBtn } from "./auth.sign-in";

export const Route = createFileRoute("/auth/sign-up")({
  component: SignUpPage,
});

function SignUpPage() {
  const status = useAuthStore((s) => s.status);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (status === "authenticated")
    return desktopRequest() ? (
      <Navigate
        to="/desktop/authorize"
        search={{ request: desktopRequest()! }}
        replace
      />
    ) : (
      <Navigate to="/" replace />
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await auth.signUp({
        email,
        password,
        name: name || undefined,
        locale: "id",
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign-up failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthShell
        title="Check your email"
        subtitle="We sent you a link to verify your address."
      >
        <p className="text-sm text-[color:var(--color-abyssal)]/70">
          Click the link in the email we just sent to <strong>{email}</strong>.
          Once you verify, you can sign in and create your workspace.
        </p>
        <div className="mt-6 text-sm">
          <Link
            to="/auth/sign-in"
            className="text-[color:var(--color-north-star)] hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="Free to start. No credit card required."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            className={inputCls}
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            className={inputCls}
          />
        </Field>
        <Field label="Password" hint="Minimum 12 characters. Long > complex.">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
            className={inputCls}
          />
        </Field>

        {error ? (
          <p className="text-sm text-[color:var(--color-coral)]">{error}</p>
        ) : null}

        <button type="submit" disabled={submitting} className={primaryBtn}>
          {submitting ? "Creating account…" : "Create account"}
        </button>

        <p className="text-center text-sm text-[color:var(--color-abyssal)]/70">
          Already have an account?{" "}
          <Link
            to="/auth/sign-in"
            className="text-[color:var(--color-north-star)] hover:underline"
          >
            Sign in
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
